// Session lifecycle: restore the stored sign-in, take tokens from the
// login.kast-dev.net hash callback, refresh before expiry and on a 401, and
// sign out through Cognito. The same shape as waypoint_wars' useSession.

import { useCallback, useEffect, useRef, useState } from "react";
import { onAuthError } from "@common/api";
import { refreshSession, storeRefreshedTokens } from "@common/auth/refreshSession";
import {
  TOKEN_EXPIRY_BUFFER_MS,
  clearTokens,
  loadTokens,
  msUntilExpiry,
  saveTokens,
  signInUrl,
  signOutUrl,
  tokensFromHash,
  userFromIdToken,
  type AuthTokens,
  type UserInfo,
} from "@common/auth/session";
import { getDeploymentConfig } from "@common/deployment-config";

export interface Session {
  user: UserInfo | null;
  signIn: () => void;
  signOut: () => void;
}

const storedUser = (): UserInfo | null => {
  const tokens = loadTokens();
  return tokens?.idToken ? userFromIdToken(tokens.idToken) : null;
};

export function useSession(): Session {
  // Seeded from storage so the first frame already shows the signed-in state.
  const [user, setUser] = useState<UserInfo | null>(storedUser);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const clearSession = useCallback(() => {
    clearTimer();
    clearTokens();
    queueMicrotask(() => setUser(null));
  }, []);

  const applyRef = useRef<(tokens: AuthTokens) => void>(() => {});

  const refresh = useCallback(
    async (refreshToken: string) => {
      const { cognitoDomain, clientId } = getDeploymentConfig();
      const result = await refreshSession({ cognitoDomain, clientId }, refreshToken);
      if (result.outcome === "ok") {
        storeRefreshedTokens(result.tokens);
        const tokens = loadTokens();
        if (tokens) applyRef.current(tokens);
        return true;
      }
      if (result.outcome === "rejected") clearSession();
      return false;
    },
    [clearSession],
  );

  const applyTokens = useCallback(
    (tokens: AuthTokens) => {
      saveTokens(tokens);
      const info = tokens.idToken ? userFromIdToken(tokens.idToken) : null;
      queueMicrotask(() => setUser(info));
      clearTimer();
      const ms = msUntilExpiry(tokens.accessToken);
      if (ms > 0 && tokens.refreshToken) {
        timer.current = setTimeout(() => void refresh(tokens.refreshToken), Math.max(ms - TOKEN_EXPIRY_BUFFER_MS, 0));
      }
    },
    [refresh],
  );

  useEffect(() => {
    applyRef.current = applyTokens;
  }, [applyTokens]);

  // Restore: tokens from the login hash win over stored ones.
  useEffect(() => {
    const fromHash = tokensFromHash(window.location.hash);
    if (fromHash) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
      applyTokens(fromHash);
      return clearTimer;
    }
    const stored = loadTokens();
    if (!stored) return;
    if (msUntilExpiry(stored.accessToken) > TOKEN_EXPIRY_BUFFER_MS) applyTokens(stored);
    else if (stored.refreshToken) void refresh(stored.refreshToken);
    else clearSession();
    return clearTimer;
  }, [applyTokens, refresh, clearSession]);

  // A 401 from the API: refresh once, else the session is gone.
  useEffect(
    () =>
      onAuthError(() => {
        const stored = loadTokens();
        if (stored?.refreshToken) void refresh(stored.refreshToken);
        else clearSession();
      }),
    [refresh, clearSession],
  );

  const signIn = useCallback(() => {
    window.location.href = signInUrl(getDeploymentConfig().loginUrl, window.location.origin + window.location.pathname);
  }, []);

  const signOut = useCallback(() => {
    const { cognitoDomain, clientId } = getDeploymentConfig();
    clearSession();
    if (cognitoDomain && clientId) window.location.href = signOutUrl(cognitoDomain, clientId, window.location.origin);
  }, [clearSession]);

  return { user, signIn, signOut };
}
